import { useRef, useState } from 'react';
import { Camera, FileText, Paperclip, Trash2 } from 'lucide-react';
import purchaseOrderService from '../../services/purchaseOrders';
import { errorMessage } from '../../utils/apiResponse';
import { useOrgTimezone, formatDateTimeInTz } from '../../utils/timezone';
import type { PODocument } from '../../types/spray';

/**
 * The payment receipts on a purchase order: a photo, or the PDF a supplier
 * emailed, of what was paid. Someone who pays for an order themselves (the
 * company card at the counter) attaches the receipt here, and accounts opens
 * it from the same card.
 *
 * Not "Receipt History" further down the order page, which is stock arriving.
 * The card's wording says "payment" throughout to keep the two apart.
 */
export default function PurchaseOrderReceipts({ poId, documents, canAdd, onChanged }: {
  poId: number;
  documents: PODocument[];
  /** Authorised for purchase orders: attaches and removes. Accounts only opens. */
  canAdd: boolean;
  onChanged: () => void;
}) {
  const tz = useOrgTimezone();
  const input = useRef<HTMLInputElement>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function upload(file: File) {
    setBusy(true);
    setError(null);
    try {
      const res = await purchaseOrderService.addReceipt(poId, file, note.trim() || undefined);
      if (res.success) {
        setNote('');
        onChanged();
      } else {
        setError(errorMessage(res, 'The receipt could not be uploaded'));
      }
    } catch (e) {
      setError(errorMessage(e, 'The receipt could not be uploaded'));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  }

  async function open(doc: PODocument) {
    setError(null);
    try {
      await purchaseOrderService.openReceipt(doc);
    } catch (e) {
      setError(errorMessage(e, 'The receipt could not be opened'));
    }
  }

  async function remove(doc: PODocument) {
    if (!confirm(`Remove ${doc.file_name} from this order?`)) return;
    setBusy(true);
    setError(null);
    try {
      const res = await purchaseOrderService.removeReceipt(doc.id);
      if (res.success) onChanged();
      else setError(errorMessage(res, 'The receipt could not be removed'));
    } catch (e) {
      setError(errorMessage(e, 'The receipt could not be removed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
      <h2 className="text-sm font-semibold text-gray-900">Payment receipts ({documents.length})</h2>
      <p className="text-xs text-gray-500 mt-1 mb-3 break-words">
        {canAdd
          ? 'Paid for this order yourself, such as on a company card? Add a photo of the receipt and accounts will have it with the order.'
          : 'A photo or PDF of what was paid, attached by whoever paid for the order.'}
      </p>

      {documents.length === 0 ? (
        <p className="text-sm text-gray-400">No payment receipt attached.</p>
      ) : (
        <ul className="space-y-2">
          {documents.map((doc) => {
            const by = [doc.uploader_first_name, doc.uploader_last_name].filter(Boolean).join(' ');
            return (
              <li key={doc.id} className="border border-gray-100 rounded-lg p-3 flex items-start gap-3">
                {doc.mime_type === 'application/pdf'
                  ? <FileText className="h-4 w-4 text-gray-400 shrink-0 mt-0.5" />
                  : <Paperclip className="h-4 w-4 text-gray-400 shrink-0 mt-0.5" />}
                <div className="min-w-0 flex-1">
                  <button type="button" onClick={() => open(doc)}
                    className="text-sm font-medium text-indigo-600 hover:underline break-all text-left">
                    {doc.file_name}
                  </button>
                  <p className="text-xs text-gray-400 break-words">
                    Added {formatDateTimeInTz(doc.created_at, tz)}{by && ` by ${by}`}
                  </p>
                  {doc.note && <p className="text-xs text-gray-600 mt-1 break-words">{doc.note}</p>}
                </div>
                {canAdd && (
                  <button type="button" onClick={() => remove(doc)} disabled={busy}
                    aria-label={`Remove ${doc.file_name}`}
                    className="text-gray-300 hover:text-red-500 disabled:opacity-50 shrink-0">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {error && (
        <div className="mt-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 break-words">{error}</div>
      )}

      {canAdd && (
        <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
          <div>
            <label htmlFor="po-receipt-note" className="block text-xs text-gray-500 mb-1">Note for accounts (optional)</label>
            <input
              id="po-receipt-note"
              type="text"
              value={note}
              maxLength={500}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Paid on the company card ending 4412"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          {/* No `capture`: a phone then offers the camera and the photo library, and a desktop a file. */}
          <input
            ref={input}
            type="file"
            accept="image/*,application/pdf"
            className="hidden"
            aria-label="Receipt photo or PDF"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); }}
          />
          <button type="button" onClick={() => input.current?.click()} disabled={busy}
            className="flex items-center gap-2 border border-indigo-300 text-indigo-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-50 disabled:opacity-50">
            <Camera className="h-4 w-4" />
            {busy ? 'Uploading…' : 'Add a receipt photo'}
          </button>
        </div>
      )}
    </div>
  );
}
